import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  BarChart3,
  Bell,
  Building2,
  Calendar,
  ChevronLeft,
  Handshake,
  LayoutDashboard,
  Map,
  Settings,
  ShieldAlert,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsCountStore } from '@/stores/notifications-count';
import { useBrandLogo } from '@/hooks/useBrandBootstrap';
import { configApi, type OrganizationProfile } from '@/services/config.api';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Identifiant pour brancher un badge dynamique (ex: notifications non lues). */
  badgeKey?: 'notifications';
}

const PRIMARY_NAV: ReadonlyArray<NavItem> = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/pipeline', label: 'Pipeline', icon: TrendingUp },
  { to: '/prospects', label: 'Prospects', icon: Building2 },
  { to: '/calendar?scope=team', label: 'Calendrier', icon: Calendar },
  { to: '/activity', label: 'Activité', icon: Activity },
  { to: '/notifications', label: 'Notifications', icon: Bell, badgeKey: 'notifications' },
  { to: '/map', label: 'Carte', icon: Map },
  { to: '/apporteurs', label: 'Apporteurs', icon: Handshake },
  { to: '/jeopardy', label: 'Jeopardy', icon: ShieldAlert },
];

const DIRECTION_NAV: ReadonlyArray<NavItem> = [
  { to: '/kpi', label: 'KPIs direction', icon: BarChart3 },
  { to: '/config', label: 'Configuration', icon: Settings },
  { to: '/audit', label: 'Audit', icon: ShieldCheck },
];

export interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.accessToken);
  const isDirection = user?.role === 'admin' || user?.role === 'director';
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || '?';

  // Profil organisation (logo + nom) - chargé au mount pour remplacer le
  // marquage par défaut « CRM Courtage / Adélie Assurances ».
  const [org, setOrg] = useState<OrganizationProfile | null>(null);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    configApi
      .getOrganization(token)
      .then((p) => {
        if (!cancelled) setOrg(p);
      })
      .catch(() => {
        if (!cancelled) setOrg(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const logoUrl = useBrandLogo(token, Boolean(org?.hasLogo));

  // Refresh du compteur de notifs non lues - au mount, au focus fenêtre et
  // au retour d'onglet. Le store est partagé avec NotificationBell (WS
  // increment) et NotificationsPage (decrement sur markRead).
  const refreshNotifCount = useNotificationsCountStore((s) => s.refresh);
  useEffect(() => {
    if (!token) return;
    void refreshNotifCount(token);
    const onFocus = () => void refreshNotifCount(token);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshNotifCount(token);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token, refreshNotifCount]);

  return (
    <aside
      className={cn(
        'hidden shrink-0 flex-col border-r border-border-subtle bg-surface transition-[width] duration-200 md:flex',
        collapsed ? 'w-[60px]' : 'w-60',
      )}
    >
      <div className="flex h-16 items-center justify-between gap-2 border-b border-border-subtle px-4">
        {!collapsed && (
          <div
            className={cn(
              'flex min-w-0 flex-1 items-center gap-2',
              org?.logoAlign === 'center' ? 'justify-center' : 'justify-start',
            )}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={org?.name ?? 'Logo'}
                className="h-8 max-w-[140px] shrink-0 object-contain"
              />
            ) : (
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-semibold text-brand">
                  {org?.name ?? 'CRM Courtage'}
                </span>
                {!org?.name && (
                  <span className="truncate text-[11px] text-text-muted">Adélie Assurances</span>
                )}
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Déplier la navigation' : 'Replier la navigation'}
          className="flex h-8 w-8 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-gray-100 hover:text-brand"
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
        </button>
      </div>

      <nav className="flex-1 space-y-0.5 px-2 py-3 text-sm">
        {PRIMARY_NAV.map((item) => (
          <SidebarLink key={item.to} item={item} collapsed={collapsed} />
        ))}

        {isDirection && (
          <>
            <SidebarDivider collapsed={collapsed} label="Direction" />
            {DIRECTION_NAV.map((item) => (
              <SidebarLink key={item.to} item={item} collapsed={collapsed} />
            ))}
          </>
        )}
      </nav>

      <div className="border-t border-border-subtle p-3">
        {/* Bloc user cliquable → /security (gestion passkeys, MFA). Pattern
            similaire au menu profil GitHub/Stripe. */}
        <NavLink
          to="/security"
          title="Sécurité du compte"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2 rounded-md p-2 transition-colors hover:bg-gray-100',
              collapsed && 'justify-center p-0',
              isActive && 'bg-gray-100',
            )
          }
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/10 text-xs font-semibold text-accent">
            {initials}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1 text-[11px]">
              <div className="truncate font-medium text-brand">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="mt-0.5 truncate capitalize text-text-muted">{user?.role}</div>
            </div>
          )}
        </NavLink>
      </div>
    </aside>
  );
}

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  const notifCount = useNotificationsCountStore((s) => s.count);
  const badgeCount = item.badgeKey === 'notifications' ? notifCount : 0;

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-md px-3 py-2 font-medium transition-colors',
          isActive ? 'bg-gray-100 text-brand' : 'text-text-muted hover:bg-gray-50 hover:text-brand',
          collapsed && 'justify-center px-0',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-accent" />
          )}
          <div className="relative">
            <Icon
              className={cn(
                'h-[18px] w-[18px] shrink-0 transition-colors',
                isActive ? 'text-accent' : 'text-text-muted group-hover:text-brand',
              )}
            />
            {/* En mode replié : petit dot rouge sur l'icône quand badge > 0. */}
            {collapsed && badgeCount > 0 && (
              <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-danger" />
            )}
          </div>
          {!collapsed && (
            <>
              <span className="flex-1">{item.label}</span>
              {badgeCount > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-danger px-1.5 text-[10px] font-semibold text-white">
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

function SidebarDivider({ collapsed, label }: { collapsed: boolean; label: string }) {
  if (collapsed) return <div className="my-3 border-t border-border-subtle" />;
  return (
    <div className="mt-5 border-t border-border-subtle pt-3 pb-1">
      <div className="px-3 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
        {label}
      </div>
    </div>
  );
}
